// Tauri adapter — the ONLY module that calls `window.__TAURI__.core.invoke`.
// Everything UI-side goes through the DesktopAdapter interface in ./index.ts.

import type { DesktopAdapter, EnterpriseConfig } from './index';

interface TauriAPI {
	core: {
		invoke: <T>(cmd: string, args?: Record<string, unknown>) => Promise<T>;
	};
	event: {
		listen: <T>(event: string, handler: (event: { payload: T }) => void) => Promise<() => void>;
	};
}

function getTauri(): TauriAPI | null {
	if (typeof window !== 'undefined' && '__TAURI__' in window) {
		return (window as unknown as { __TAURI__: TauriAPI }).__TAURI__;
	}
	return null;
}

export const tauriAdapter: DesktopAdapter = {
	async getEnterpriseConfig(): Promise<EnterpriseConfig | null> {
		const tauri = getTauri();
		if (!tauri) return null;
		return tauri.core.invoke<EnterpriseConfig | null>('get_enterprise_config');
	},

	async setEnterpriseConfig(serverUrl: string, token: string): Promise<void> {
		const tauri = getTauri();
		if (!tauri) return;
		await tauri.core.invoke('set_enterprise_config', { serverUrl, token });
	},

	async exchangeAuthCode(serverUrl: string, code: string): Promise<{ token: string }> {
		const tauri = getTauri();
		if (!tauri) throw new Error('Tauri runtime not available');
		// The Rust command persists the config itself; we mirror the adapter
		// contract by returning the token if the caller wants it.
		const result = await tauri.core.invoke<{ token?: string } | null>('exchange_auth_code', {
			serverUrl,
			code,
		});
		return { token: result?.token ?? '' };
	},

	async switchAccount(): Promise<void> {
		const tauri = getTauri();
		if (!tauri) return;
		await tauri.core.invoke('switch_account');
	},

	async showLauncher(): Promise<void> {
		const tauri = getTauri();
		if (!tauri) return;
		await tauri.core.invoke('show_launcher');
	},
};
